import { useEffect, useMemo, useState } from "react";
import { Search, ExternalLink, ArrowRight, MapPin } from "lucide-react";
import { http } from "@/lib/api";

const norm = (t) => (t || "").toLowerCase();
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" }) : null);

const flatten = (mods, items) => {
  const out = [];
  (mods?.courses || []).forEach((c) => c.modules.forEach((m) => m.items.filter((it) => it.type !== "SubHeader").forEach((it) =>
    out.push({ key: `m-${c.id}-${it.id}`, title: it.title, path: [c.name, m.name], url: it.url, due: it.due_at, done: it.completed, locked: it.locked, todo: !!it.requirement && !it.completed && !it.locked, kind: it.type }))));
  items.filter((i) => i.source === "school").forEach((i) =>
    out.push({ key: `s-${i.id}`, title: i.topic, path: i.where ? i.where.path.slice(0, 2) : [i.course], url: i.where?.url || i.link, due: i.due_at || `${i.date}T23:59:00`, done: i.done, todo: i.status === "due", kind: "Assignment" }));
  return out;
};

const Row = ({ r, testId }) => (
  <li data-testid={testId}>
    <a href={r.url || undefined} target="_blank" rel="noreferrer" className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#F7F3EB] transition-colors group">
      <span className="flex-1 min-w-0"><span className={`block text-sm font-medium ${r.done ? "line-through text-stone-400" : "text-[#1F3D33]"}`}>{r.title}</span>
        <span className="flex items-start gap-1 text-[11px] text-stone-500"><MapPin className="w-3 h-3 shrink-0 mt-0.5" /><span className="break-words sm:truncate">{r.path.join(" › ")}</span></span></span>
      {r.due && <span className="text-[11px] text-[#1F5C99] whitespace-nowrap">{fmt(r.due)}</span>}
      {r.locked && <span className="text-[11px] text-stone-400">Locked</span>}
      {r.url && <ExternalLink className="w-3.5 h-3.5 text-[#1F5C99] opacity-60 group-hover:opacity-100" />}
    </a>
  </li>
);

export const FindAssignment = ({ d }) => {
  const [mods, setMods] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { http.get(`/canvas-modules/${d.student_id}`).then((r) => setMods(r.data)).catch(() => {}); }, [d.student_id]);
  const all = useMemo(() => {
    const seen = new Set();
    return flatten(mods, d.agenda.concat(d.items)).filter((r) => { const k = `${norm(r.title)}|${r.path[0]}`; if (seen.has(k)) return false; seen.add(k); return true; });
  }, [mods, d]);
  const hits = q.trim().length > 1 ? all.filter((r) => norm(`${r.title} ${r.path.join(" ")}`).includes(norm(q.trim()))).slice(0, 12) : null;
  const [more, setMore] = useState(false);
  const ts = (r) => (r.due ? new Date(r.due).getTime() : Infinity);
  const todo = all.filter((r) => r.todo).sort((a, b) => ts(a) - ts(b));
  const next = more ? todo : todo.slice(0, 5);
  if (!all.length) return null;
  return (
    <section className="mt-10 rounded-3xl bg-white border border-[#E6DEC3] p-6 md:p-8" data-testid="find-assignment">
      <h2 className="font-display text-3xl text-[#2C4A3E]">Find my assignment</h2>
      <p className="text-sm text-stone-500 mt-1">Search every course, module and assignment. Tap one to open it right in Canvas.</p>
      <label className="mt-4 flex items-center gap-2 px-4 py-3 rounded-2xl bg-[#FBF8F1] border border-[#E6DEC3] focus-within:border-[#2C4A3E] transition-colors">
        <Search className="w-4 h-4 text-stone-500" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Try “quiz”, “essay” or a course name" aria-label="Find my assignment" data-testid="find-assignment-input" className="flex-1 bg-transparent text-sm focus:outline-none" />
      </label>
      {hits ? (
        <ul className="mt-3 max-h-[360px] overflow-y-auto" data-testid="find-assignment-results">
          {hits.map((r) => <Row key={r.key} r={r} testId={`find-hit-${r.key}`} />)}
          {!hits.length && <li className="px-3 py-4 text-sm text-stone-500" data-testid="find-assignment-empty">No matches. Try a shorter word.</li>}
        </ul>
      ) : next.length > 0 && (
        <div className="mt-5" data-testid="do-next">
          <p className="text-xs uppercase tracking-widest text-[#C87D53] font-semibold inline-flex items-center gap-1"><ArrowRight className="w-3.5 h-3.5" />Do this next</p>
          <ul className="mt-2 max-h-[420px] overflow-y-auto">{next.map((r) => <Row key={r.key} r={r} testId={`do-next-${r.key}`} />)}</ul>
          {todo.length > 5 && <button onClick={() => setMore(!more)} data-testid="do-next-more" className="mt-2 text-xs underline text-[#C87D53]">{more ? "Show less" : `Show all ${todo.length}`}</button>}
        </div>
      )}
    </section>
  );
};
